import React from 'react';
import { observer } from 'mobx-react';
import { SynonymCardWrapper } from '@seedlang/hoc';
import styled from '@emotion/styled';
import CardSection from 'components/vocab_card/card_section';
import MediaSection from 'components/vocab_card/media_section';
import { DeckUI } from '@seedlang/state';
import CardWrapper from 'components/layout/card_wrapper';
import CardContent from 'components/layout/card_content';
import CardSourceList from 'components/vocab_card/card_source_list';
import { Theme } from '@seedlang/constants';
import { QuestionIcon } from '@seedlang/icons';
import { pixify } from '@seedlang/utils';
import MultipleChoiceButtons from 'components/card/multiple_choice_buttons';
import ExpandedTarget from 'components/vocab_card/expanded_target';
import ExampleSentence, { ExampleSentenceToggle } from 'components/vocab_card/example_sentence';
import { ExampleSentenceTranslationProvider } from '@seedlang/hoc';
import EvaluateButtons from 'components/card/evaluate_buttons';
import Text from 'components/text';
import CardArrowBox from 'components/card/card_arrow_box';

const IconWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: ${Theme.z['card-3']};
  .fa-times {
    color: ${Theme.red};
  }
  .fa-check {
    color: ${Theme.green};
  }
`;

const HintButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: ${Theme.z['card-3']};
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: #FFF;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12), 0 3px 1px -2px rgba(0, 0, 0, 0.2);
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  svg {
    width: 26px;
    height: 26px;
    fill: #333;
  }
`;

const TextWrapper = styled.div`
  width: 100%;
  background: #FFF;
  border-radius: 10px;
  padding: 10px;
`;

const ExampleSentenceFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 10px;
  right: 10px;
  max-height: 35%;
  overflow-y: auto;
`;

@observer
class SynonymCard extends React.Component {
  onClickTargetSection = () => {
    DeckUI.openDeckVocabCard({
      card: this.props.card,
      cardState: this.props.cardState,
      requiresRevealed: true,
    });
  }

  render() {
    const hasVideoInCurrentState = this.props.revealed ? this.props.correctSynonymHasVideoClip : this.props.promptWordHasVideoClip;
    const unrevealedContentHeight = DeckUI.layout.contentHeightWithMultipleChoiceButtons(this.props.synonymOptionsCount);
    const promptWordSectionHeight = this.props.revealed
      ? null
      : (this.props.promptWordHasVideoClip ? unrevealedContentHeight / 2 : unrevealedContentHeight);
    const mediaHeight = unrevealedContentHeight / 2;
    return (
      <ExampleSentenceTranslationProvider
        parts={this.props.example?.cardExampleSentence}
        translation={this.props.example?.cardExampleTranslation}
        languageId={this.props.example?.languageId}
        revealed={this.props.revealed}
        allowTranslation={this.props.sentenceHintShown}
        visible={this.props.showExampleSentence}
      >
      <CardWrapper
        card={this.props.card}
        cardState={this.props.cardState}
        userCard={this.props.userCard}
        icons={this.props.icons}
      >
        <CardContent
          padding={0}
          height={pixify(this.props.revealed ? DeckUI.layout.contentHeight : DeckUI.layout.contentHeightWithMultipleChoiceButtons(this.props.synonymOptionsCount))}
        >
          {
            !this.props.revealed && this.props.promptWordHasVideoClip &&
              <MediaSection
                autoPlay={this.props.inView}
                showMedia
                borderTopRadius={10}
                card={this.props.card}
                cardState={this.props.cardState}
                word={this.props.promptWord}
                height={mediaHeight}
              />
          }
          {
            this.props.revealed && this.props.correctSynonymHasVideoClip &&
              <MediaSection
                autoPlay={this.props.inView}
                showMedia
                borderTopRadius={10}
                card={this.props.card}
                cardState={this.props.cardState}
                word={this.props.revealedWord}
                height={mediaHeight}
              />
          }
          <CardSection
            background='#FFF'
            height={this.props.revealed ? null : promptWordSectionHeight}
            borderTopRadius={!hasVideoInCurrentState ? 10 : 0}
            borderBottomRadius={this.props.revealed ? 0 : 10}
            numSections={hasVideoInCurrentState ? 3 : 2}
            onClick={DeckUI.canOpenDeckVocabCard(this.props.card, this.props.cardState, true) ? this.onClickTargetSection : null}
          >
            <ExampleSentenceToggle />
            {
              this.props.revealed && this.props.userCardIsUsable &&
                <IconWrapper>
                  {
                    this.props.userCard.correct &&
                      <i className='fa fa-check' />
                  }
                  {
                    !this.props.userCard.correct &&
                      <i className='fa fa-times' />
                  }
                </IconWrapper>
            }
            {this.props.showSentenceHintButton &&
              <HintButton aria-label='Show Hint' title='Show Hint' onClick={this.props.showSentenceHint}>
                <QuestionIcon hideOutline />
              </HintButton>
            }
            <TextWrapper>
              {
                !this.props.revealed && this.props.showPromptText &&
                <Text
                  fontSize='30px'
                  bold
                >
                  {this.props.promptWordText}
                </Text>
              }
              {
                this.props.revealed &&
                <ExpandedTarget
                  card={this.props.card}
                  cardState={this.props.cardState}
                  word={this.props.revealedWord || this.props.card.word}
                  grammarTextSize={16}
                  grammarFontWeight='bold'
                  hideConjugation
                  hidePlural
                  description={this.props.synonymDescription}
                  descriptionBackgroundColor='#FFF'
                  descriptionTextColor={Theme.darkGray}
                  descriptionItalic
                  descriptionMargin='5px 0 0 0'
                  hideDescriptionIcon
                />
              }
            </TextWrapper>
            {
              this.props.showExampleSentence &&
                <ExampleSentenceFooter>
                  <ExampleSentence
                    key={this.props.example.id}
                    parts={this.props.example.cardExampleSentence}
                    translation={this.props.example.cardExampleTranslation}
                    languageId={this.props.example.languageId}
                    revealed={this.props.revealed}
                    allowTranslation={this.props.sentenceHintShown}
                    color='#333'
                  />
                </ExampleSentenceFooter>
            }
          </CardSection>
          {
            this.props.revealed &&
              <CardSection
                background='#CCC'
                borderBottomRadius={10}
                numSections={hasVideoInCurrentState ? 3 : 2}
                height={null}
              >
                <CardSourceList
                  showEdit
                  word={this.props.revealedWord || this.props.card.word}
                  card={this.props.card}
                />
              </CardSection>
          }
          <CardArrowBox
            card={this.props.card}
            possibleArrowBoxes={this.props.possibleArrowBoxes}
            currentArrowBox={this.props.currentArrowBox}
          />
        </CardContent>
        {
          !this.props.revealed &&
            <MultipleChoiceButtons
              blockFontCalculation
              width={DeckUI.layout.innerDeckWidth}
              height={pixify(DeckUI.layout.totalMultipleChoiceButtonHeight(this.props.synonymOptionsCount))}
              buttonHeight={DeckUI.layout.multipleChoiceButtonHeight}
              selected={[this.props.multipleChoiceSelected]}
              correct={this.props.correct}
              options={this.props.synonymOptions}
              onClick={val => this.props.onClick(val)}
              isMobile={DeckUI.layout.isMobile}
              field='value'
              padding='5px'
              margin='0'
              fontSize={DeckUI.layout.minimumHeight ? '13px' : '16px'}
              disabled={!this.props.inView}
              languageId={this.props.card.languageId}
            />
        }
        {
          this.props.revealed && this.props.userCardIsUsable &&
            <EvaluateButtons
              card={this.props.card}
              onNextClick={() => DeckUI.advanceCard(this.props.card, this.props.cardState, this.props.userCard)}
              cardState={this.props.cardState}
              userCard={this.props.userCard}
              disabled={!this.props.inView}
            />
        }
      </CardWrapper>
      </ExampleSentenceTranslationProvider>
    );
  }
}

export default SynonymCardWrapper(SynonymCard);
